import React from 'react';
import { useSelector } from 'react-redux';

// Selectors
import { userLanguagePreferenceSelector } from '../../../../../../user/currentUserSelector';
import {
    LegacyActiveHotTableSelector,
    useLegacyActiveTableSelector,
} from '../../../../../../element/table/store/legacyHotTableStoreHooks';
import { selectLegacyActiveTableCellSelection } from '../../../../../../element/table/store/selectors/legacyActiveHotTableSelectors';

// Components
import ToolbarPopupTool from '../../../ToolbarPopupTool';
import Icon from '../../../../../../components/icons/Icon';
import LegacyTableCellAlignmentToolbarPopup from './LegacyTableCellAlignmentToolbarPopup';

// Utils
import { getAllCellsBetween } from '../../../../../../element/table/legacy/utils/tableCellSelectionUtils';
import { getCellTextAlignment } from '../../../../../../element/table/legacy/utils/tableCellFormattingUtils';

// Constants
import { PopupIds, PopupSections } from '../../../../../../components/popupPanel/popupConstants';
import { TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT } from '../../../../../../../common/table/tableConstants';

/**
 * Determines the text alignment that is common across all selected cells.
 * If there's no common alignment, returns undefined.
 */
const selectCommonTextAlignment: LegacyActiveHotTableSelector<string | undefined, { locale: string }> = (
    activeTable,
    { locale },
) => {
    if (!activeTable) return;

    const currentCellSelections = selectLegacyActiveTableCellSelection(activeTable);

    if (!currentCellSelections || currentCellSelections.length === 0) return;

    const allCells = getAllCellsBetween(currentCellSelections);
    const firstCell = allCells[0];
    const { cellData } = activeTable.getCellMeta(firstCell.row, firstCell.col);
    const displayValue = activeTable.getDataAtCell(firstCell.row, firstCell.col);

    const firstTextAlignment = getCellTextAlignment(cellData, locale, displayValue);

    if (!firstTextAlignment) return;

    const hasDifferentTextAlignments = allCells.some(({ row, col }) => {
        const { cellData } = activeTable.getCellMeta(row, col);
        const displayValue = activeTable.getDataAtCell(row, col);

        const textAlignment = getCellTextAlignment(cellData, locale, displayValue);
        return textAlignment !== firstTextAlignment;
    });

    if (hasDifferentTextAlignments) return;

    return firstTextAlignment;
};

/**
 * Determines the vertical alignment that is common across all selected cells.
 * If there's no common alignment, returns the default vertical alignment.
 */
const selectCommonVerticalAlignment: LegacyActiveHotTableSelector<string | undefined> = (activeTable) => {
    if (!activeTable) return;

    const currentCellSelections = selectLegacyActiveTableCellSelection(activeTable);

    if (!currentCellSelections || currentCellSelections.length === 0) return;

    const allCells = getAllCellsBetween(currentCellSelections);
    const { cellData: firstCellData } = activeTable.getCellMeta(allCells[0].row, allCells[0].col);
    const firstVerticalAlignment = firstCellData?.verticalAlignment;

    if (!firstVerticalAlignment) return TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT;

    const hasDifferentVerticalAlignments = allCells.some(({ row, col }) => {
        const { cellData } = activeTable.getCellMeta(row, col);
        return cellData?.verticalAlignment !== firstCellData?.verticalAlignment;
    });

    if (hasDifferentVerticalAlignments) return TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT;

    return firstVerticalAlignment;
};

const selectIsDisabled: LegacyActiveHotTableSelector<boolean> = (activeTable) => {
    const selection = selectLegacyActiveTableCellSelection(activeTable);
    return !selection || selection.length === 0;
};

const LegacyTableCellAlignmentTool: React.FC = (props) => {
    const locale = useSelector(userLanguagePreferenceSelector);

    // Get common text alignment of all selected cells
    const commonTextAlignment = useLegacyActiveTableSelector((activeTable) =>
        selectCommonTextAlignment(activeTable, { locale }),
    );

    // Get common vertical alignment of all selected cells
    const commonVerticalAlignment = useLegacyActiveTableSelector(selectCommonVerticalAlignment);
    const isDisabled = useLegacyActiveTableSelector(selectIsDisabled);

    return (
        <ToolbarPopupTool
            {...props}
            name="Alignment"
            popupToolClassName="LegacyTableTool"
            popupId={PopupIds.TABLE_CELL_ALIGNMENT_TOOL}
            closeSection={PopupSections.TOOLBAR}
            toolName={'CellAlignment'}
            disabled={isDisabled}
            buttonSelector=".CellalignmentTool .icon"
            PopupComponent={LegacyTableCellAlignmentToolbarPopup}
            preventFocus
            // @ts-ignore Struggling with the JSX type inference
            active={false}
            commonTextAlignment={commonTextAlignment}
            commonVerticalAlignment={commonVerticalAlignment}
        >
            <Icon name={`toolbar-align-${commonTextAlignment?.toLowerCase() || 'justified'}`} />
        </ToolbarPopupTool>
    );
};

export default LegacyTableCellAlignmentTool;
